import { useEffect, useState } from 'react'
import { TeamDetailsData } from '@dc-shared-web/hooks/api/team'
import { User } from '@dc-shared-web/hooks/api/user'
import {
  DOCUMENT_CRUNCH_EMAIL_DOMAIN,
  HIGH_SECURITY_ENTITLEMENT,
  HIGH_SECURITY_WARNING_STORAGE_KEY_PREFIX,
} from '@/app/core/constants/security'

/**
 * Hook to manage the display of high security team warnings for Document Crunch employees
 * @param team - The team data to check for high_security entitlement
 * @param user - The user data to check for @documentcrunch.com email
 * @returns shouldShowWarning - Whether the warning should be displayed
 * @returns handleDismiss - Function to dismiss the warning
 */
export const useHighSecurityTeamWarning = (
  team: TeamDetailsData | undefined,
  user: User
) => {
  const [shouldShowWarning, setShouldShowWarning] = useState(false)

  useEffect(() => {
    if (!team || !user) {
      setShouldShowWarning(false)
      return
    }

    const isDocumentCrunchEmployee = user.email.endsWith(
      `@${DOCUMENT_CRUNCH_EMAIL_DOMAIN}`
    )

    const hasHighSecurity = team.entitlements?.some(
      (entitlement) => entitlement.name === HIGH_SECURITY_ENTITLEMENT
    )

    const isUserMemberOfTeam = user.teams.some(
      (userTeam) => userTeam.id === team.uuid
    )

    const storageKey = `${HIGH_SECURITY_WARNING_STORAGE_KEY_PREFIX}-${team.uuid}`
    let isDismissed = false

    try {
      isDismissed = sessionStorage.getItem(storageKey) === 'true'
    } catch (error) {
      // sessionStorage may not be available in private browsing mode
      isDismissed = false
    } finally {
      setShouldShowWarning(
        isDocumentCrunchEmployee &&
          hasHighSecurity &&
          !isUserMemberOfTeam &&
          !isDismissed
      )
    }
  }, [team, user])

  const handleDismiss = () => {
    if (team) {
      const storageKey = `${HIGH_SECURITY_WARNING_STORAGE_KEY_PREFIX}-${team.uuid}`
      try {
        sessionStorage.setItem(storageKey, 'true')
      } catch (error) {
        // sessionStorage may not be available in private browsing mode
        // Still dismiss the warning for this component instance
      }
      setShouldShowWarning(false)
    }
  }

  return { shouldShowWarning, handleDismiss }
}
